import clsx from 'clsx';

import { Link } from './Link';
import { Title } from './Title';
import { Typography } from './Typography';

interface ListItem {
  title: string;
  content?: string;
  link?: string;
  list?: string[]
}

interface NumberedListProps {
  variant: 'vertical' | 'horizontal';
  title?: Title;
  content?: Markdown;
  data?: ListItem[];
  size?: 'default' | 'large';
  itemMarker?: 'numbers' | 'arrow';
}

export const NumberedList: React.FC<NumberedListProps> = ({
  variant,
  title,
  content,
  data,
  size = 'default',
  itemMarker = 'numbers',
}) => (
  <div className="numbered-list flex flex-col gap-16">
    <div
      className={clsx(
        'mx-auto flex max-w-[37rem] flex-col text-center',
        variant === 'horizontal' && 'gap-6',
      )}
    >
      <Title variant="center" data={title} spacing="1" />

      <Typography variant="subtitle1" className="max-w-[37rem]" markdown>
        {content}
      </Typography>
    </div>

    <ol
      className={clsx(
        'flex gap-8',
        variant === 'vertical' && 'mx-auto flex-col',
        variant === 'horizontal' && 'flex-wrap justify-center',
      )}
      data-variant={variant}
    >
      {data?.map((item, index) => {
        const Content = (
          <Typography
            variant="body1"
            className={clsx(
              'font-semibold text-neutral-900',
              variant === 'horizontal' && 'text-xl',
              variant === 'vertical' && 'whitespace-nowrap',
              item.link && 'hover:text-primary-400',
            )}
            markdown
          >
            {variant === 'vertical' && item.content
              ? `${item.title}.`
              : item.title}
          </Typography>
        );

        return (
          <li
            key={index}
            className={clsx(
              'flex items-center',
              variant === 'horizontal' &&
                'min-w-[16rem] flex-col gap-5 text-center',
              variant === 'horizontal' &&
                size === 'default' &&
                'md:min-w-[17.5rem] md:max-w-[17.5rem]',
              variant === 'horizontal' &&
                size === 'large' &&
                'md:min-w-[24rem] md:max-w-[24rem]',
              variant === 'vertical' &&
                'flex-col text-center md:flex-row md:text-left',
            )}
            data-lz={itemMarker === 'numbers' && variant === 'vertical'}
            data-arrow={itemMarker === 'arrow'}
          >
            {item?.link?.href ? (
              <Link href={item?.link?.href}>{Content}</Link>
            ) : (
              Content
            )}
            {item.content && (
              <Typography
                variant="subtitle1"
                className={clsx(
                  variant === 'horizontal' && 'text-zinc-800',
                  variant === 'vertical' &&
                    'ml-1 text-base text-neutral-500 md:line-clamp-1',
                )}
                markdown
              >
                {item.content}
              </Typography>
            )}
            {
              item?.list?.map((elem, index) => <p key={`${item.title}-${index}`}>{elem}</p>)
            }
          </li>
        );
      })}
    </ol>
  </div>
);
